/** Tuition operations are independent of marketing copy and learning access. */
export const TUITION_SUBJECT_IDS = [
  'mathematics',
  'english',
  'chemistry',
] as const;
export type TuitionSubject = (typeof TUITION_SUBJECT_IDS)[number];
export type SubjectStreams = Partial<Record<TuitionSubject, string | null>>;
export const CHEMISTRY_COURSES = ['pure', 'combined'] as const;
export type ChemistryCourse = (typeof CHEMISTRY_COURSES)[number];
export const CHEMISTRY_COURSE_LABELS: Record<ChemistryCourse, string> = {
  pure: 'Pure Chemistry',
  combined: 'Combined Science (Chemistry)',
};
export const TUITION_SUBJECT_LABELS: Record<TuitionSubject, string> = {
  mathematics: 'Mathematics',
  english: 'English',
  chemistry: 'Chemistry',
};
/** The class subject, including the Chemistry course or component where known. */
export function tuitionSubjectLabel(
  subject: TuitionSubject,
  stream?: string | null,
  chemistryCourse?: ChemistryCourse | null
): string {
  if (subject !== 'chemistry') return TUITION_SUBJECT_LABELS[subject];
  if (stream === 'G2') return 'Science (Chemistry)';
  if (stream === 'G3' && chemistryCourse)
    return CHEMISTRY_COURSE_LABELS[chemistryCourse];
  return 'Chemistry';
}
export interface TuitionSelection {
  tuitionSubjects?: readonly TuitionSubject[] | null;
  subjectStreams?: SubjectStreams | null;
  chemistryCourse?: ChemistryCourse | null;
  secondaryStream?: string | null;
  subjects?: readonly string[] | null;
  level?: string | null;
}
const CHEMISTRY_LEVELS = ['secondary_3', 'secondary_4'];
const CHEMISTRY_STREAMS = ['G2', 'G3', 'IP'];

/** Lead drafts may omit details, but known Chemistry choices must fit the offer. */
export function chemistryLevelError(value: TuitionSelection): string | null {
  const selection = normalizeTuitionSelection(value);
  if (!selection.tuitionSubjects.includes('chemistry')) return null;
  if (value.level && !CHEMISTRY_LEVELS.includes(value.level))
    return 'Chemistry is offered for Secondary 3 or Secondary 4';
  const stream = selection.subjectStreams.chemistry;
  if (stream && !CHEMISTRY_STREAMS.includes(stream))
    return 'Choose G2, G3 or IP for Chemistry';
  return null;
}

export function chemistryCourseError(
  value: TuitionSelection,
  requireG3Course = false
): string | null {
  if (
    value.chemistryCourse != null &&
    !CHEMISTRY_COURSES.includes(value.chemistryCourse)
  )
    return 'Chemistry course must be Pure Chemistry or Combined Science (Chemistry)';
  const selection = normalizeTuitionSelection(value);
  if (!selection.tuitionSubjects.includes('chemistry')) return null;
  const stream = selection.subjectStreams.chemistry;
  if (stream === 'G2' && value.chemistryCourse === 'pure')
    return 'G2 Science (Chemistry) is the Chemistry component of a two-science subject';
  if (stream === 'G3' && !selection.chemistryCourse && requireG3Course)
    return 'Choose G3 Pure Chemistry or G3 Combined Science (Chemistry)';
  return null;
}
export function normalizeTuitionSelection(value: TuitionSelection) {
  const tuitionSubjects = TUITION_SUBJECT_IDS.filter(subject =>
    (value.tuitionSubjects ?? ['mathematics']).includes(subject)
  );
  const subjectStreams: SubjectStreams = {};
  for (const subject of tuitionSubjects) {
    subjectStreams[subject] =
      value.subjectStreams?.[subject] !== undefined
        ? (value.subjectStreams[subject] ?? null)
        : ((subject === 'mathematics' ? value.secondaryStream : null) ?? null);
  }
  return {
    tuitionSubjects,
    subjectStreams,
    ...(value.chemistryCourse !== undefined
      ? {
          chemistryCourse:
            tuitionSubjects.includes('chemistry') &&
            subjectStreams.chemistry === 'G3'
              ? value.chemistryCourse
              : null,
        }
      : {}),
  };
}
export function hasMathematics(value: TuitionSelection): boolean {
  return normalizeTuitionSelection(value).tuitionSubjects.includes(
    'mathematics'
  );
}
export function isPublicTuitionCombination(
  subject: TuitionSubject,
  level?: string | null,
  stream?: string | null
): boolean {
  if (subject === 'mathematics') return true;
  const levels =
    subject === 'english'
      ? ['secondary_1', 'secondary_2', 'secondary_3', 'secondary_4']
      : CHEMISTRY_LEVELS;
  const streams =
    subject === 'english' ? ['G1', 'G2', 'G3', 'IP', 'IB'] : CHEMISTRY_STREAMS;
  return levels.includes(level ?? '') && (!stream || streams.includes(stream));
}
export function tuitionCourseLabels(value: TuitionSelection): string[] {
  const selection = normalizeTuitionSelection(value);
  return selection.tuitionSubjects.flatMap(subject => {
    if (subject !== 'mathematics') {
      const stream = selection.subjectStreams[subject];
      const label = tuitionSubjectLabel(subject, stream, value.chemistryCourse);
      return [
        subject === 'chemistry' && (stream === 'G2' || stream === 'G3')
          ? `${stream} ${label}`
          : label,
      ];
    }
    const papers = ['E-Math', 'A-Math'].filter(paper =>
      value.subjects?.includes(paper)
    );
    return papers.length ? papers : ['Mathematics'];
  });
}
export function formatTuitionProgramme(value: TuitionSelection): string {
  const selection = normalizeTuitionSelection(value);
  return selection.tuitionSubjects
    .map(subject => {
      const stream = selection.subjectStreams[subject];
      const label = tuitionSubjectLabel(subject, stream, value.chemistryCourse);
      const papers =
        subject === 'mathematics'
          ? value.subjects?.filter(s => ['E-Math', 'A-Math'].includes(s))
          : [];
      return [
        ...(subject === 'chemistry' ? [stream, label] : [label, stream]),
        papers?.length ? `(${papers.join(' + ')})` : null,
      ]
        .filter(Boolean)
        .join(' ');
    })
    .join(' · ');
}
export function classTuitionSubject(value: {
  tuitionSubject?: TuitionSubject | null;
}): TuitionSubject {
  return value.tuitionSubject ?? 'mathematics';
}
export function classMatchesTuition(
  value: TuitionSelection,
  classCode: {
    tuitionSubject?: TuitionSubject | null;
    examLevel?: string | null;
    chemistryCourse?: ChemistryCourse | null;
  }
): boolean {
  const subject = classTuitionSubject(classCode);
  const selection = normalizeTuitionSelection(value);
  if (!selection.tuitionSubjects.includes(subject)) return false;
  if (subject !== 'chemistry') return true;
  const selectedCourse =
    selection.subjectStreams.chemistry === 'G2'
      ? 'combined'
      : selection.chemistryCourse;
  const classCourse =
    classCode.examLevel === 'G2' ? 'combined' : classCode.chemistryCourse;
  return (!selectedCourse && !classCourse) || selectedCourse === classCourse;
}
/** A stable identity for retry comparisons, independent of field/key order. */
export function tuitionSelectionKey(value: TuitionSelection): string {
  const selection = normalizeTuitionSelection(value);
  return JSON.stringify({
    level: value.level,
    ...selection,
    chemistryCourse: selection.chemistryCourse ?? null,
    subjects: hasMathematics(value) ? [...(value.subjects ?? [])].sort() : [],
  });
}
/** Merge a partial edit without erasing other subjects or their streams. */
export function mergeTuitionSelection(
  current: TuitionSelection,
  patch: TuitionSelection
): TuitionSelection {
  const previous = normalizeTuitionSelection(current);
  return {
    ...current,
    ...patch,
    ...(previous.chemistryCourse !== undefined ||
    patch.chemistryCourse !== undefined
      ? {
          chemistryCourse:
            patch.chemistryCourse !== undefined
              ? patch.chemistryCourse
              : previous.chemistryCourse,
        }
      : {}),
    tuitionSubjects: patch.tuitionSubjects ?? previous.tuitionSubjects,
    subjectStreams: {
      ...previous.subjectStreams,
      ...(patch.secondaryStream !== undefined
        ? { mathematics: patch.secondaryStream }
        : {}),
      ...patch.subjectStreams,
    },
  };
}
/** Mongo query fragment: missing/null metadata is a legacy Mathematics record. */
export function tuitionSubjectFilter(
  subject: TuitionSubject
): Record<string, unknown> {
  return subject === 'mathematics'
    ? { $or: [{ tuitionSubjects: subject }, { tuitionSubjects: null }] }
    : { tuitionSubjects: subject };
}
